// Source of truth: platform-docs/legal/TERMS_OF_USE.md
// When bumping TOS_VERSION (in Register.js + the canonical .md), mirror the
// content changes here. The .md file is the legally canonical version.

import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const TermsOfUse = () => (
  <div className="legal-page">
    <Link to="/login" className="legal-back">&larr; Back</Link>

    <h1>Terms of Use</h1>
    <div className="legal-meta">
      <div><strong>Operator:</strong> Maia AI, LLC</div>
      <div><strong>Version:</strong> v1.0</div>
      <div><strong>Effective date:</strong> 2026-04-27</div>
      <div><strong>Last updated:</strong> 2026-04-27</div>
    </div>

    <hr />

    <h2>1. Who we are and how this agreement is formed</h2>
    <p>
      These Terms of Use (the "<strong>Terms</strong>") are an agreement between <strong>Maia AI, LLC</strong> ("<strong>Maia</strong>," "<strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>") and you, the person or entity using the service described below (the "<strong>User</strong>" or "<strong>you</strong>"). The Terms govern your access to and use of the Maia service.
    </p>
    <p>
      You accept these Terms by creating an account, by placing or receiving calls through the service, by configuring the service to act on your behalf, or by otherwise using the service. If you do not accept these Terms, you must not use the service.
    </p>
    <p>
      We may update these Terms from time to time. The current version is identified by the "Last updated" date above. Material changes will be communicated through the service or sent to the email address registered to your account. Your continued use of the service after the effective date of an update constitutes acceptance of the updated Terms.
    </p>

    <hr />

    <h2>2. The service</h2>
    <p>
      Maia is an AI-assisted phone agent. On your instruction, the service places telephone calls on your behalf, navigates interactive-voice-response ("IVR") systems and live-agent conversations, and produces transcripts and written summaries of those calls.
    </p>
    <p>
      The service relies on third-party providers for telephony, speech-to-text, and large-language-model inference. Your use of the service is also subject to those providers' terms, which we will identify in our public documentation. We do not control, and are not responsible for, the conduct of those third parties.
    </p>
    <p>
      The service is provided as a tool to assist you. You remain responsible for the content of the calls placed on your behalf, for the outcomes of those calls, and for any fees or charges incurred in connection with those calls — including, without limitation, charges from telecommunications carriers, long-distance or international charges, premium-rate numbers, and any third-party services accessed during the call.
    </p>

    <hr />

    <h2>3. Eligibility and account responsibility</h2>
    <p>
      You may only use the service if you have the legal capacity to enter into this agreement. The minimum age to use the service is <strong>18 years old</strong>. If you are using the service on behalf of an organization, you represent that you have authority to bind that organization to these Terms; in that case "you" refers to both you individually and the organization.
    </p>
    <p>
      You may maintain only one account per legal person or entity, except where we expressly permit otherwise in writing.
    </p>
    <p>You are responsible for everything that happens under your account. This includes:</p>
    <ul>
      <li>The accuracy and lawfulness of the contacts, instructions, and other inputs you configure.</li>
      <li>Any call placed or received by the service on your behalf, regardless of whether you supervised the call in real time, and any actions taken, transactions completed, or commitments made as a result of those calls — including, without limitation, payments authorized, services scheduled, accounts modified, and information disclosed.</li>
      <li>Maintaining the confidentiality of your account credentials and notifying us promptly of any suspected unauthorized access.</li>
    </ul>

    <hr />

    <h2>4. Acceptable use</h2>
    <p>
      You must not use the service, and must not configure the service to act on your behalf, in any way that is or that facilitates any of the following. The list is illustrative and not exhaustive.
    </p>

    <h3>4.1 Illegal activity</h3>
    <p>
      You must not use the service in any way that violates United States federal, state, or local law, or the law of any jurisdiction in which you, your account, or a call recipient is located. Prohibited uses include, without limitation: wire fraud, harassment, stalking, threats, controlled-substance trafficking, money laundering, or any other unlawful financial activity.
    </p>

    <h3>4.2 Telecommunications-law violations</h3>
    <p>
      You must not use the service in any way that would cause Maia or its telephony providers to violate telecommunications law or carrier acceptable-use policies. Prohibited uses include, without limitation:
    </p>
    <ul>
      <li>Robocalling, auto-dialing, or campaigning practices that violate the Telephone Consumer Protection Act, the Telemarketing Sales Rule, FCC rules, or carrier terms.</li>
      <li>Caller-ID spoofing in violation of the Truth in Caller ID Act, the TRACED Act, or the STIR/SHAKEN regime.</li>
      <li>Calling a number after the recipient has asked, on a prior call or in writing, not to be contacted.</li>
    </ul>

    <h3>4.3 Deceptive impersonation</h3>
    <p>
      You must not configure the service to deceive call recipients about its nature as an AI agent in any context where law, regulation, or carrier policy requires that disclosure. You must not impersonate a specific individual, government official, law-enforcement officer, court, regulator, or business other than yourself or an entity you are duly authorized to represent.
    </p>

    <h3>4.4 Harassment, abuse, and harmful content</h3>
    <p>
      You must not use the service to harass, threaten, defame, or abuse any person. You must not transmit obscene content, content that targets a person on the basis of a protected characteristic, or content that constitutes child sexual abuse material, support for terrorism, or any other category of content prohibited by law or by our third-party providers' policies.
    </p>

    <h3>4.5 Fraud and unauthorized access</h3>
    <p>
      You must not use the service to attempt to obtain account access, credentials, financial information, benefits, or services from any third party — including IVR systems, banks, insurers, utilities, employers, healthcare providers, or government agencies — that you are not legitimately entitled to access or to receive on your own behalf or on behalf of a person or entity that has authorized you. Prohibited tactics include, without limitation, credential stuffing, social-engineering of human agents, and circumvention of identity-verification controls.
    </p>

    <h3>4.6 Service abuse</h3>
    <p>
      You must not (a) reverse-engineer, decompile, or otherwise attempt to extract the source or model weights underlying the service; (b) scrape, automate, or otherwise extract data from the service except through interfaces we provide for that purpose; (c) attempt to disrupt, degrade, or interfere with the service or any other user's use of it, including through denial-of-service attacks or by exceeding rate limits we communicate to you; or (d) resell, sublicense, or repackage the service or its output as a separate product without our prior written authorization.
    </p>

    <h3>4.7 Violations of third-party rights</h3>
    <p>
      You must not use the service to infringe any third party's intellectual-property, privacy, publicity, or contractual rights, including by configuring the service to disclose information you are under an obligation to keep confidential.
    </p>

    <h3>4.8 Catch-all</h3>
    <p>
      We reserve the right to determine, in our reasonable judgment, that any other use of the service is incompatible with these Terms or with the integrity of the service, and to treat that use as a violation of section 4.
    </p>

    <hr />

    <h2>5. Forfeiture and termination</h2>
    <p>
      <strong>Any violation of section 4 forfeits your right to continued use of the service.</strong> We may suspend or terminate your account immediately, without prior notice and without any obligation to refund fees, transition data, or assist with migration, upon a good-faith determination that a violation has occurred or is imminent.
    </p>
    <p>
      <strong>You also forfeit, with respect to use that occurred in violation of these Terms, all other rights and remedies you would otherwise have under this agreement.</strong> In particular, you waive any claim against Maia AI, LLC for damages, lost data, lost calls, lost contacts, business interruption, lost profits, or any other loss arising out of the suspension or termination of an account that we have determined was used in violation of these Terms.
    </p>
    <p>
      We may, in connection with a termination under this section, retain or delete your account data — including contacts, call history, transcripts, and audio — subject to applicable law.
    </p>
    <p>
      We may cooperate with law-enforcement, regulator, or carrier inquiries arising from violations of these Terms, including by providing the records described in section 6 below, in response to a lawful request or where we determine in good faith that disclosure is necessary to address suspected wrongdoing or a threat to safety.
    </p>
    <p>
      You may terminate your account at any time. Termination by you does not relieve you of any obligation that arose before termination, and the provisions of these Terms that by their nature should survive termination — including sections 5, 6, 7, 8, 10, and 11 — survive.
    </p>

    <hr />

    <h2>6. Records access for service protection</h2>
    <p>
      This section grants Maia a right of access to records of your activity that is <strong>independent of, and broader than,</strong> any user-initiated, consent-bounded data-sharing flow we may offer (such as the user-initiated "flag this call" flow used to improve the AI). The flag flow is for product research, redacts sensitive values before any transcript leaves your private record, and depends on your consent. <strong>This section is different.</strong> It is the right we reserve to protect the service, our other users, and third parties from misconduct.
    </p>

    <h3>6.1 Grant of access</h3>
    <p>
      You grant Maia AI, LLC a broad, ongoing right to <strong>access, review, retain, and analyze</strong> records of your activity on the service whenever we have a good-faith reason to investigate, prevent, or respond to:
    </p>
    <p>
      (a) a suspected violation of these Terms;<br />
      (b) a suspected violation of law by you or by a person using your account; or<br />
      (c) a threat to the safety, integrity, or lawful operation of the service or to any person.
    </p>

    <h3>6.2 What "records" includes</h3>
    <p>For the purposes of this section, "<strong>records</strong>" includes, without limitation:</p>
    <ul>
      <li>account metadata (registration details, login history, device and IP information);</li>
      <li>the contacts you have configured;</li>
      <li>the instructions, prompts, and other inputs you have given the service;</li>
      <li><strong>call audio</strong>, including recordings of any calls placed or received through the service;</li>
      <li><strong>call transcripts, including unredacted transcripts</strong>, regardless of whether sensitive values have been redacted in copies of those transcripts that are shown to you in the user interface or shared into product-improvement pipelines;</li>
      <li>the AI system's prompts and responses produced during the service's operation on your behalf;</li>
      <li>audit logs and any other technical telemetry generated by the service.</li>
    </ul>

    <h3>6.3 Limits on use</h3>
    <p>
      We will exercise the right granted in this section only as reasonably necessary for the purposes stated in section 6.1. Access will be limited to personnel — and to automated systems operating on our behalf — that have a legitimate need to know, and we will handle records consistent with the privacy policy and with applicable law.
    </p>

    <h3>6.4 Survival</h3>
    <p>
      This grant survives termination of your account for as long as is reasonably necessary to complete an investigation under section 6.1, to respond to legal process, or to defend Maia AI, LLC against a claim arising from your use of the service.
    </p>

    <h3>6.5 Relationship to the privacy policy and consent flows</h3>
    <p>
      Nothing in this section displaces commitments we have made in our privacy policy or in the in-product consent text that governs user-initiated data sharing. Where this section and those documents address the same use of records, the more privacy-protective standard applies — except where exercising this section is reasonably necessary for the purposes in section 6.1, in which case this section controls.
    </p>

    <hr />

    <h2>7. Your content and data</h2>
    <p>
      You retain ownership of the contacts, instructions, and other inputs you provide. You grant us a worldwide, non-exclusive, royalty-free license to host, store, process, transmit, and otherwise use those inputs as reasonably necessary to provide the service to you and, subject to the privacy policy and any applicable consent flows, to maintain and improve the service.
    </p>
    <p>
      The handling of personal information is governed by our <Link to="/legal/privacy">privacy policy</Link>, which is incorporated into these Terms by reference. The privacy policy describes what we collect, how long we retain it, and the choices available to you. In the event of a conflict between these Terms and the privacy policy with respect to handling of personal information, the privacy policy controls — except with respect to section 6 of these Terms, which is a service-protection right and is not displaced by the privacy policy.
    </p>

    <hr />

    <h2>8. Disclaimers and limitation of liability</h2>

    <h3>8.1 "As is"</h3>
    <p>
      The service is provided "as is" and "as available." We do not warrant that any call placed through the service will succeed, that any IVR system will be navigated correctly, that any task or goal you set for the service will be achieved, or that the service will be uninterrupted or error-free. AI-generated output may be incorrect, incomplete, or misleading. <strong>You are responsible for verifying the outcome of any sensitive action — financial, medical, legal, or otherwise — before relying on it.</strong>
    </p>
    <p>
      To the maximum extent permitted by law, we disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
    </p>

    <h3>8.2 Limitation of liability</h3>
    <p>
      To the maximum extent permitted by law, Maia AI, LLC and its affiliates, officers, directors, employees, agents, and service providers shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, or for any loss of profits, revenue, data, goodwill, or business opportunities, arising out of or related to your use of or inability to use the service, even if we have been advised of the possibility of such damages.
    </p>
    <p>
      To the maximum extent permitted by law, the aggregate liability of Maia AI, LLC for all claims arising out of or related to the service or these Terms shall not exceed the total amount of fees paid by you to Maia AI, LLC in the twelve (12) months immediately preceding the event giving rise to the claim.
    </p>
    <p>
      The limitations in this section apply regardless of the form of action, whether in contract, tort, strict liability, or otherwise.
    </p>
    <p>
      Nothing in these Terms limits or excludes liability that cannot be limited or excluded under applicable law.
    </p>

    <h3>8.3 Indemnification</h3>
    <p>
      You agree to defend, indemnify, and hold harmless Maia AI, LLC and its affiliates, officers, directors, employees, agents, and service providers from and against any and all claims, demands, actions, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
    </p>
    <p>
      (a) your use of the service;<br />
      (b) your violation of these Terms, including any breach of Section 4;<br />
      (c) any content, instructions, or data you provide to or through the service; or<br />
      (d) your violation of any law or the rights of any third party.
    </p>
    <p>
      Maia AI, LLC reserves the right, at your expense, to assume the exclusive defense and control of any matter subject to indemnification by you, and you agree to cooperate fully in the defense of such matter. You may not settle any claim without our prior written consent if the settlement imposes any obligation or liability on Maia AI, LLC.
    </p>

    <hr />

    <h2>9. Changes to these Terms</h2>
    <p>
      We may update these Terms at any time. The current version is the one posted at the canonical URL or location we maintain for the Terms, identified by the "Last updated" date in the header. For material changes, we will provide notice through the service or by email to the address registered to your account at least <strong>14 days</strong> before the change takes effect.
    </p>
    <p>
      If you do not agree to a change, your sole remedy is to stop using the service and to terminate your account before the change takes effect. Your continued use of the service on or after the effective date of the change constitutes acceptance of the updated Terms.
    </p>

    <hr />

    <h2>10. Governing law and disputes</h2>
    <p>
      These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions.
    </p>

    <h3>10.1 Agreement to arbitrate</h3>
    <p>
      To the maximum extent permitted by law, any dispute, claim, or controversy arising out of or relating to these Terms or the use of the service (each, a "<strong>Dispute</strong>") shall be resolved by binding, individual arbitration, rather than in court, except as expressly provided below.
    </p>
    <p>
      This arbitration agreement is governed by the Federal Arbitration Act (9 U.S.C. §§ 1–16).
    </p>

    <h3>10.2 Scope of arbitration</h3>
    <p>
      Arbitration applies to all Disputes, whether based in contract, tort, statute, fraud, misrepresentation, or any other legal theory, and includes disputes regarding the interpretation, applicability, or enforceability of this arbitration agreement, including any claim that all or part of this section is void or voidable.
    </p>

    <h3>10.3 Exceptions to arbitration</h3>
    <p>Notwithstanding the foregoing:</p>
    <p>
      (a) Either party may bring an individual claim in small claims court if the claim qualifies; and<br />
      (b) Maia AI, LLC may seek injunctive or equitable relief in any court of competent jurisdiction to protect its intellectual property, confidential information, or to prevent abuse or misuse of the service.
    </p>

    <h3>10.4 Arbitration procedures</h3>
    <p>
      Arbitration shall be administered by a nationally recognized arbitration provider selected by Maia AI, LLC, under its applicable rules. Unless otherwise required by the provider's rules or applicable law:
    </p>
    <ul>
      <li>Arbitration will be conducted on an individual basis only;</li>
      <li>The arbitration will be conducted remotely (including by video conference or written submissions), unless the arbitrator determines an in-person hearing is necessary;</li>
      <li>The arbitrator shall have authority to award any relief available in court on an individual basis.</li>
    </ul>

    <h3>10.5 Class-action waiver</h3>
    <p>
      To the maximum extent permitted by law, all Disputes must be brought solely in an individual capacity, and not as a plaintiff or class member in any purported class, collective, consolidated, or representative proceeding.
    </p>
    <p>
      The arbitrator shall have no authority to conduct any class, collective, or representative proceeding or to consolidate the claims of multiple parties.
    </p>

    <h3>10.6 Jury trial waiver</h3>
    <p>
      To the maximum extent permitted by law, you and Maia AI, LLC waive any right to a trial by jury in any proceeding arising out of or relating to these Terms or the service.
    </p>

    <h3>10.7 Arbitration opt-out</h3>
    <p>
      You may opt out of this arbitration agreement by providing written notice to support@maiatech.ai within 30 days of first accepting these Terms. Your notice must include your name, the email address associated with your account, and a clear statement that you wish to opt out of arbitration. If you opt out, this Section 10.1 will not apply, but the remainder of this Section 10 will continue to apply.
    </p>

    <h3>10.8 Venue for non-arbitrable disputes</h3>
    <p>
      To the extent any Dispute is not subject to arbitration, the state and federal courts located in Travis County, Austin, Texas shall have exclusive jurisdiction, and you consent to the personal jurisdiction of such courts.
    </p>

    <h3>10.9 Non-waivable rights</h3>
    <p>
      Nothing in this Section limits or excludes any rights that cannot be waived or limited under applicable law.
    </p>

    <hr />

    <h2>11. Contact</h2>
    <p>
      Notices to Maia AI, LLC under these Terms — including legal, abuse, and privacy notices — should be sent to:
    </p>
    <p>
      <strong>Maia AI, LLC</strong><br />
      Email: support@maiatech.ai
    </p>
    <p>
      We will use the email address registered to your account as the primary channel for notices to you under these Terms.
    </p>

    <hr />

    <h2>12. General</h2>

    <h3>12.1 Entire agreement</h3>
    <p>
      These Terms, together with the privacy policy and any other agreements we identify as incorporated by reference, are the entire agreement between you and Maia AI, LLC concerning the service and supersede any prior agreement on the same subject.
    </p>

    <h3>12.2 Severability</h3>
    <p>
      If any provision of these Terms is held unenforceable, the remaining provisions remain in effect and the unenforceable provision will be enforced to the maximum extent permitted by law.
    </p>

    <h3>12.3 No waiver</h3>
    <p>
      Our failure to enforce any provision of these Terms is not a waiver of our right to enforce it later.
    </p>

    <h3>12.4 Assignment</h3>
    <p>
      You may not assign or transfer these Terms or any rights under them without our prior written consent. We may assign these Terms in connection with a merger, acquisition, reorganization, or sale of assets, or by operation of law.
    </p>

    <h3>12.5 Force majeure</h3>
    <p>
      Neither party is liable for any failure or delay caused by events beyond its reasonable control, including acts of God, war, civil unrest, governmental action, labor disputes, network or carrier outages, or failures of third-party providers.
    </p>

    <h3>12.6 Relationship of the parties</h3>
    <p>
      These Terms do not create any agency, partnership, joint venture, or employment relationship between you and Maia AI, LLC.
    </p>

    <p className="legal-end">End of Terms.</p>
  </div>
);

export default TermsOfUse;
